import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { isTenantAccessEnabled } from "@/lib/tenant-access";

/**
 * Standard Role System (v2)
 * 
 * 5 roles: owner, manager, doctor, front_desk, assistant
 * + access_mode: full | audit
 * + clinic scope: which clinics the user is assigned to
 * 
 * This hook reads from the `standard_role` and `access_mode` columns
 * in `user_clinic_roles`, which coexist with the legacy `role` column
 * during the migration period.
 */

export type StandardRole = 'owner' | 'manager' | 'doctor' | 'front_desk' | 'assistant';
export type AccessMode = 'full' | 'audit';

export interface ClinicRoleAssignment {
  clinicId: string;
  standardRole: StandardRole;
  accessMode: AccessMode;
  legacyRole?: string | null;
  tenantId?: string | null;
}

export interface TenantAccessStatus {
  tenantId: string | null;
  tenantName: string | null;
  isEnabled: boolean;
  status: string | null;
  reason: string | null;
  suspendedAt: string | null;
  gracePeriodEndsAt: string | null;
}

export interface StandardRoleData {
  /** All clinic assignments for this user */
  assignments: ClinicRoleAssignment[];
  
  /** The highest role across all assignments (owner > manager > doctor > front_desk > assistant) */
  highestRole: StandardRole | null;
  
  /** Whether the user is a super_admin (platform level) */
  isSuperAdmin: boolean;
  
  /** Whether the user has the owner role in any clinic */
  isOwner: boolean;
  
  /** Whether the user has the manager role in any clinic */
  isManager: boolean;
  
  /** Whether the user has the doctor role in any clinic */
  isDoctor: boolean;
  
  /** Whether the user has the front_desk role in any clinic */
  isFrontDesk: boolean;
  
  /** Whether the user has the assistant role in any clinic */
  isAssistant: boolean;
  
  /** Whether the user has audit-only access in any clinic */
  hasAuditMode: boolean;
  
  /** Check if user has a specific role for a specific clinic */
  hasRoleForClinic: (role: StandardRole, clinicId: string) => boolean;
  
  /** Check if user can write (non-audit) to a specific clinic */
  canWriteToClinic: (clinicId: string) => boolean;
  
  /** Check if user can manage (owner/manager with full access) a specific clinic */
  canManageClinic: (clinicId: string) => boolean;
  
  /** Get all clinic IDs this user has access to */
  accessibleClinicIds: string[];

  /** Minimal tenant status needed by the app shell pause guard */
  tenantAccess: TenantAccessStatus;
  
  /** Financial authority checks */
  canVoidBill: (clinicId: string, amount: number) => boolean;
  canApplyDiscount: (clinicId: string, percentage: number) => boolean;
  canDeleteBill: (clinicId: string) => boolean;
}

const ROLE_HIERARCHY: Record<StandardRole, number> = {
  owner: 5,
  manager: 4,
  doctor: 3,
  front_desk: 2,
  assistant: 1,
};


const toMaybeSingle = <T,>(value: T | T[] | null | undefined): T | null => {
  if (Array.isArray(value)) return value[0] ?? null;
  return value ?? null;
};

export const useStandardRole = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["standard-role", user?.id],
    queryFn: async (): Promise<StandardRoleData | null> => {
      if (!user?.id) return null;

      // Check if user is super_admin (platform level)
      const { data: appRoles } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id);

      const isSuperAdmin = appRoles?.some(r => r.role === "super_admin") || false;

      const { data: actorProfileId, error: actorError } = await supabase.rpc("clinical_continuity_actor_profile_id");
      if (actorError) throw actorError;
      // Get all clinic role assignments with standard_role
      const { data: clinicRoleData, error } = actorProfileId ? await supabase
        .from("user_clinic_roles")
        .select(`
          clinic_id,
          role,
          standard_role,
          access_mode,
          clinic:clinics(
            tenant_id,
            tenant:tenants(
              id,
              name,
              is_active,
              subscription_status,
              trial_ends_at,
              subscription_grace_period_ends_at,
              suspended_at,
              suspension_reason
            )
          )
        `)
        .eq("user_id", actorProfileId) : { data: [], error: null };

      if (error) {
        console.error("[useStandardRole] Error fetching roles:", error);
        throw error;
      }

      const roleRows = (clinicRoleData || []) as any[];
      const assignments: ClinicRoleAssignment[] = roleRows
        .filter(r => r.standard_role) // Only include rows with the new standard_role set
        .map(r => {
          const clinic = toMaybeSingle(r.clinic);
          return {
            clinicId: r.clinic_id,
            standardRole: r.standard_role as StandardRole,
            accessMode: (r.access_mode || 'full') as AccessMode,
            legacyRole: r.role ?? null,
            tenantId: clinic?.tenant_id ?? null,
          };
        });

      const tenantRows = roleRows
        .map(r => toMaybeSingle(toMaybeSingle(r.clinic)?.tenant))
        .filter(Boolean);
      const primaryTenant = tenantRows[0] ?? null;
      const primaryTenantStatus = primaryTenant?.subscription_status ?? null;
      const tenantAccess: TenantAccessStatus = isSuperAdmin
        ? {
            tenantId: primaryTenant?.id ?? null,
            tenantName: primaryTenant?.name ?? null,
            isEnabled: true,
            status: primaryTenantStatus,
            reason: primaryTenant?.suspension_reason ?? null,
            suspendedAt: primaryTenant?.suspended_at ?? null,
            gracePeriodEndsAt: primaryTenant?.subscription_grace_period_ends_at ?? null,
          }
        : {
            tenantId: primaryTenant?.id ?? null,
            tenantName: primaryTenant?.name ?? null,
            isEnabled: isTenantAccessEnabled(primaryTenant),
            status: primaryTenantStatus,
            reason: primaryTenant?.suspension_reason ?? null,
            suspendedAt: primaryTenant?.suspended_at ?? null,
            gracePeriodEndsAt: primaryTenant?.subscription_grace_period_ends_at ?? null,
          };

      // Determine highest role
      let highestRole: StandardRole | null = null;
      let highestPriority = 0;
      for (const a of assignments) {
        const priority = ROLE_HIERARCHY[a.standardRole] || 0;
        if (priority > highestPriority) {
          highestPriority = priority;
          highestRole = a.standardRole;
        }
      }

      // Role flags
      const isOwner = assignments.some(a => a.standardRole === 'owner');
      const isManager = assignments.some(a => a.standardRole === 'manager');
      const isDoctor = assignments.some(a => a.standardRole === 'doctor');
      const isFrontDesk = assignments.some(a => a.standardRole === 'front_desk');
      const isAssistant = assignments.some(a => a.standardRole === 'assistant');
      const hasAuditMode = assignments.some(a => a.accessMode === 'audit');

      // Helper functions
      const hasRoleForClinic = (role: StandardRole, clinicId: string) =>
        assignments.some(a => a.clinicId === clinicId && a.standardRole === role);

      const canWriteToClinic = (clinicId: string) =>
        isSuperAdmin || assignments.some(a =>
          a.clinicId === clinicId &&
          a.accessMode === 'full' &&
          a.standardRole !== 'assistant'
        );

      const canManageClinic = (clinicId: string) =>
        isSuperAdmin || assignments.some(a =>
          a.clinicId === clinicId &&
          a.accessMode === 'full' &&
          (a.standardRole === 'owner' || a.standardRole === 'manager')
        );

      const accessibleClinicIds = [...new Set(assignments.map(a => a.clinicId))];

      // Financial authority
      const canVoidBill = (clinicId: string, amount: number) => {
        if (isSuperAdmin || isOwner) return true;
        const clinicAssignment = assignments.find(a =>
          a.clinicId === clinicId && a.accessMode === 'full'
        );
        if (!clinicAssignment) return false;
        if (clinicAssignment.standardRole === 'manager' && amount <= 5000) return true;
        return false;
      };

      const canApplyDiscount = (clinicId: string, percentage: number) => {
        if (isSuperAdmin || isOwner) return true;
        const clinicAssignment = assignments.find(a =>
          a.clinicId === clinicId && a.accessMode === 'full'
        );
        if (!clinicAssignment) return false;
        if (clinicAssignment.standardRole === 'manager' && percentage <= 25) return true;
        if (['doctor', 'front_desk'].includes(clinicAssignment.standardRole) && percentage <= 10) return true;
        return false;
      };

      const canDeleteBill = (clinicId: string) => {
        if (isSuperAdmin || isOwner) return true;
        return false; // Only owner/super_admin can delete bills
      };

      return {
        assignments,
        highestRole,
        isSuperAdmin,
        isOwner,
        isManager,
        isDoctor,
        isFrontDesk,
        isAssistant,
        hasAuditMode,
        hasRoleForClinic,
        canWriteToClinic,
        canManageClinic,
        accessibleClinicIds,
        tenantAccess,
        canVoidBill,
        canApplyDiscount,
        canDeleteBill,
      };
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

/**
 * Tenant to stamp on owner-authored rows: the tenant of this user's
 * owner + full-access assignment — the SAME source the RLS owner checks use
 * (user_clinic_roles.standard_role), NOT useTenantId()/tenant_user_roles.
 * Null for non-owners (e.g. super admins); DB insert-stamp triggers cover that.
 */
export const useAuthoringTenantId = (): string | null => {
  const { data } = useStandardRole();
  const owner = data?.assignments.find(
    (a) => a.standardRole === 'owner' && a.accessMode === 'full'
  );
  return owner?.tenantId ?? null;
};
